import { forwardRef, memo, useEffect, useImperativeHandle, useRef } from 'react';
import { ForgeViewer } from '../../helpers/forge-viewer';
import { ForgeViewerEvents } from '../../helpers/forge-viewer.enums';
import { AutodeskForgeViewerRef } from '../../helpers/forge-viewer.types';

export interface ForgeViewerEvent {
    type: string;
    target?: any;
    [key: string]: any;
}

export interface ForgeViewerExtensionEvent extends ForgeViewerEvent {
    extension: Autodesk.Viewing.Extension;
    extensionId: string;
}

export interface AutodeskForgeViewerProps {
    accessToken: string;
    urn: string;
    onInitialized?: (viewer: ForgeViewer, event: ForgeViewerEvent) => void;
    onModelAdded?: (viewer: ForgeViewer, event: ForgeViewerEvent) => void;
    onModelLoaded?: (viewer: ForgeViewer, event: ForgeViewerEvent) => void;
    onObjectTreeCreated?: (viewer: ForgeViewer, event: ForgeViewerEvent) => void;
    onTexturesLoaded?: (viewer: ForgeViewer, event: ForgeViewerEvent) => void;
    onGeometryLoaded?: (viewer: ForgeViewer, event: ForgeViewerEvent) => void;
    onToolChange?: (viewer: ForgeViewer, event: ForgeViewerEvent) => void;
    onExtensionLoaded?: (viewer: ForgeViewer, event: ForgeViewerExtensionEvent) => void;
    onExtensionActivated?: (viewer: ForgeViewer, event: ForgeViewerExtensionEvent) => void;
    onExtensionDeactivated?: (viewer: ForgeViewer, event: ForgeViewerExtensionEvent) => void;
    onViewerResize?: (viewer: ForgeViewer, event: ForgeViewerEvent) => void;
    onDestroyed?: (viewer: ForgeViewer, event: ForgeViewerEvent) => void;
    onInteract?: () => void;
}

const AutodeskForgeViewer = forwardRef<AutodeskForgeViewerRef, AutodeskForgeViewerProps>((props, ref) => {
    const {
        accessToken,
        urn,
        onInitialized,
        onModelAdded,
        onModelLoaded,
        onObjectTreeCreated,
        onTexturesLoaded,
        onGeometryLoaded,
        onToolChange,
        onExtensionLoaded,
        onExtensionActivated,
        onExtensionDeactivated,
        onViewerResize,
        onDestroyed,
        onInteract,
    } = props;

    const isMountedRef = useRef(false);
    const forgeViewerRef = useRef<ForgeViewer>(null);

    useImperativeHandle(ref, () => ({
        get instance() {
            return forgeViewerRef.current;
        },
    }));

    const forgeViewerContainerRef = useRef(null);

    useEffect(() => {
        if (isMountedRef.current) return;

        if (!forgeViewerContainerRef.current) return;

        const viewer = new ForgeViewer(forgeViewerContainerRef.current, {
            accessToken,
            urn,
        });

        viewer.init();

        forgeViewerRef.current = viewer;

        isMountedRef.current = true;

        const isMounted = isMountedRef.current;

        return () => {
            if (isMounted) return;

            if (viewer) {
                viewer.destroy();
            }
        };
    }, [accessToken, urn]);

    useEffect(() => {
        if (!forgeViewerRef.current) return;

        const eventHandlers: Array<{ event: ForgeViewerEvents; handler: (e?: any) => void }> = [
            {
                event: ForgeViewerEvents.INITIALIZED,
                handler: (e) => onInitialized?.(forgeViewerRef.current, e),
            },
            {
                event: ForgeViewerEvents.MODEL_ADDED,
                handler: (e) => onModelAdded?.(forgeViewerRef.current, e),
            },
            {
                event: ForgeViewerEvents.MODEL_LOADED,
                handler: (e) => onModelLoaded?.(forgeViewerRef.current, e),
            },
            {
                event: ForgeViewerEvents.OBJECT_TREE_CREATED,
                handler: (e) => onObjectTreeCreated?.(forgeViewerRef.current, e),
            },
            {
                event: ForgeViewerEvents.TEXTURES_LOADED,
                handler: (e) => onTexturesLoaded?.(forgeViewerRef.current, e),
            },
            {
                event: ForgeViewerEvents.GEOMETRY_LOADED,
                handler: (e) => onGeometryLoaded?.(forgeViewerRef.current, e),
            },
            {
                event: ForgeViewerEvents.TOOL_CHANGE,
                handler: (e) => onToolChange?.(forgeViewerRef.current, e),
            },
            {
                event: ForgeViewerEvents.EXTENSION_LOADED,
                handler: (e) => onExtensionLoaded?.(forgeViewerRef.current, e),
            },
            {
                event: ForgeViewerEvents.EXTENSION_ACTIVATED,
                handler: (e) => onExtensionActivated?.(forgeViewerRef.current, e),
            },
            {
                event: ForgeViewerEvents.EXTENSION_DEACTIVATED,
                handler: (e) => onExtensionDeactivated?.(forgeViewerRef.current, e),
            },
            {
                event: ForgeViewerEvents.VIEWER_RESIZE,
                handler: (e) => onViewerResize?.(forgeViewerRef.current, e),
            },
            {
                event: ForgeViewerEvents.DESTROYED,
                handler: (e) => onDestroyed?.(forgeViewerRef.current, e),
            },
        ];

        eventHandlers.forEach(({ event, handler }) => {
            forgeViewerRef.current.on(event, handler);
        });

        const forgeViewer = forgeViewerRef.current;

        return () => {
            eventHandlers.forEach(({ event, handler }) => {
                forgeViewer.off(event, handler);
            });
        };
    }, [
        onInitialized,
        onModelAdded,
        onModelLoaded,
        onObjectTreeCreated,
        onTexturesLoaded,
        onGeometryLoaded,
        onToolChange,
        onExtensionLoaded,
        onExtensionActivated,
        onExtensionDeactivated,
        onViewerResize,
        onDestroyed,
    ]);

    return (
        <>
            <div
                className="adsk-viewing-viewer notouch dark-theme quality-text absolute inset-0 w-full h-full z-0 outline-none"
                ref={forgeViewerContainerRef}
                onWheel={onInteract}
                onPointerDown={onInteract}
                onKeyDown={onInteract}
            ></div>
        </>
    );
});

export default memo(AutodeskForgeViewer);
